import React from "react";
import { motion } from "framer-motion";



// Placeholder for images from the upload
const imageMap = {
  "Fixed-Deposit": "/assets/invest/Fixed-Deposit.jpg", // Replace with your actual path
  "Publishing-Partners": "/assets/invest/Publishing-Partners.jpg", // Replace with your actual path
  "Logistics-Partners": "/assets/invest/Logistics-Partners.jpg", // Replace with your actual path
  "Convertible-Notes": "/assets/invest/Convertible-Notes.jpg", // Replace with your actual path
  "Revenue-Sharing": "/assets/invest/Revenue-Sharing.jpg", // Replace with your actual path
  "Equity-Investment": "/assets/invest/Equity-Investment.jpg", // Replace with your actual path
};

// Define a primary color for the component (placeholder: indigo-500)
const PRIMARY_COLOR_CLASS = "text-indigo-600";
const ACCENT_COLOR_CLASS = "bg-indigo-600";
const ACCENT_TEXT_CLASS = "text-indigo-600";
const ACCENT_BG_CLASS = "bg-indigo-600";

const investmentOptions = [
  {
    image: imageMap["Equity-Investment"],
    title: "Equity Investment",
    subtitle: "Seed / Pre-Series A",
    description: "Own a stake in our growth. Ideal for angel investors, VCs, and publishing partners.",
    details: "₹2L – ₹2Cr investment range",
    tag: "Growth Equity",
    gradient: "from-green-500 to-teal-700",
  },
  {
    image: imageMap["Revenue-Sharing"],
    title: "Revenue Sharing",
    subtitle: "Fixed Income Model",
    description: "Earn fixed income with regular payouts. No equity required.",
    details: "Short- to mid-term investors",
    tag: "Recurring Returns",
    gradient: "from-blue-500 to-indigo-700",
  },
  {
    image: imageMap["Convertible-Notes"],
    title: "Convertible Notes",
    subtitle: "SAFE Agreements",
    description: "Flexible startup funding with delayed dilution.",
    details: "Perfect for HNIs and accelerators",
    tag: "Future Equity",
    gradient: "from-purple-500 to-fuchsia-700",
  },
  {
    image: imageMap["Logistics-Partners"],
    title: "Logistics Partners",
    subtitle: "Distribution Network",
    description: "Co-branded delivery, shared warehousing & revenue share.",
    details: "Courier companies & delivery startups",
    tag: "Expansion Support",
    gradient: "from-orange-500 to-red-700",
  },
  {
    image: imageMap["Publishing-Partners"],
    title: "Publishing Partners",
    subtitle: "EdTech Collaborations",
    description: "Support literacy via subscription kits and CSR campaigns.",
    details: "Regional publishing focus",
    tag: "Social Impact",
    gradient: "from-amber-500 to-yellow-700",
  },
  {
    image: imageMap["Fixed-Deposit"],
    title: "Fixed Deposit",
    subtitle: "Secure Investment",
    description: "Earn 12–15% p.a. + up to 5% bonus with quarterly payouts.",
    details: "₹50K – ₹1.99L investment range",
    tag: "Low Risk, High Return",
    gradient: "from-red-500 to-pink-700",
  },
];

const InvestmentOpportunitiesSection = () => {
  return (
    <section id="investment-opportunities" className="relative py-24 px-6 md:px-12 bg-gray-50 overflow-hidden">
      {/* Background abstract shape - Modern and subtle */}
      <div className="absolute inset-0 opacity-10 [mask-image:radial-gradient(ellipse_at_center,white,transparent)] pointer-events-none">
        <div className="w-full h-full bg-[radial-gradient(#d3e0fc_1px,transparent_1px)] [background-size:16px_16px]"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className={`text-sm font-semibold uppercase tracking-widest ${ACCENT_TEXT_CLASS} mb-2`}>
            Investment Models
          </p>
          <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Strategic Funding <span className={ACCENT_TEXT_CLASS.replace('text-', '')}>Opportunities</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Explore our curated models tailored for institutional, private, and partnership-driven capital.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
          {investmentOptions.map((option, idx) => (
            <motion.div
              key={idx}
              className="group relative h-96 overflow-hidden rounded-2xl cursor-pointer transform transition-all duration-500 hover:-translate-y-1 hover:shadow-xl shadow-lg border border-gray-100"
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              viewport={{ once: true }}
            >
              {/* Card Image Background */}
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-[1.03] group-hover:brightness-110" 
                style={{ backgroundImage: `url(${option.image})` }}
              >
                {/* Image Overlay: Dark and slightly transparent, lightens on hover */}
                <div className="absolute inset-0 bg-black/60 group-hover:bg-black/40 transition-all duration-500"></div>
              </div>

              {/* Content Layer (Glassmorphism Effect) */}
              <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-8">
                {/* Tag */}
                <span className={`inline-flex self-start text-xs font-semibold px-3 py-1 rounded-full text-white mb-3 bg-gradient-to-r ${option.gradient}`}>
                  {option.tag}
                </span>

                {/* Title & Subtitle */}
                <h3 className="text-2xl font-bold text-white mb-1 drop-shadow-lg">
                  {option.title}
                </h3>
                <p className="text-base text-gray-200 mb-4">{option.subtitle}</p>
                
                {/* Description & Details */}
                <p className="text-gray-300 mb-4 text-sm leading-relaxed border-t border-white/20 pt-4">
                  {option.description}
                </p>

                {/* Details Callout */}
                <div className="flex items-center text-sm font-medium text-white opacity-90 transition-opacity duration-300 group-hover:opacity-100">
                  <svg className={`w-4 h-4 mr-2 ${ACCENT_TEXT_CLASS.replace('text-', 'text-')}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                  </svg>
                  {option.details}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InvestmentOpportunitiesSection;